import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { fetchSpecimens } from "../api";
import { flagsFor } from "../flags";
import { CheckIcon, MenuIcon, RoomIcon, SunIcon } from "../icons";
import { photoFor, productPhotoFor } from "../photos";
import { LanguageToggle, useLanguage } from "../i18n";
import { ErrorState, LoadingState } from "../components/States";
import { LoggerProvider, useLogger } from "../logger";
import { fetchProducts, type Product } from "../productsApi";
import { QuickLog } from "../components/QuickLog";
import { NotAsking } from "../components/NotAsking";
import { WrongPlace } from "../components/WrongPlace";
import { UndoBar } from "../components/UndoBar";
import { fetchPlan, type Plan, type Task } from "../tasksApi";
import { useReasonList, type Reason } from "../reasons";
import type { Specimen } from "../types";

/** How many whole days past due, or null if it is not late. */
function daysOver(dueAt: string, at: string): number | null {
  const over = Math.floor((new Date(at).getTime() - new Date(dueAt).getTime()) / 86_400_000);
  return over >= 1 ? over : null;
}

/** Turns the day count into something worth reading on a phone at 7am. */
function since(days: number | null, t: ReturnType<typeof useLanguage>["t"]): string {
  if (days === null) return t.nothingLogged;
  if (days === 0) return t.lastCheckedToday;
  if (days === 1) return t.lastCheckedYesterday;
  return t.lastCheckedDaysAgo(days);
}

/**
 * The heat spike is why the whole day moved, not why one plant did, so it is
 * lifted out of the rows it shortened and shown once, above the day.
 */
function heatReason(plan: Plan): Reason | null {
  for (const group of plan.locations) {
    for (const task of group.tasks) {
      const found = (task.reasons ?? []).find((reason) => reason.code === "heat_spike");
      if (found) return found;
    }
  }
  return null;
}

/**
 * The ask line already says the last watering is unknown, so repeating it in
 * the reasons is noise, and the heat spike has its own strip at the top.
 */
function worthShowing(reasons: Reason[] | null | undefined): Reason[] {
  return (reasons ?? []).filter(
    (reason) => reason.code !== "last_watering_unknown" && reason.code !== "heat_spike",
  );
}

/** Collects a Location's tasks by plant, preserving the order they arrived. */
function byPlant(tasks: Task[]): Array<[string, Task[]]> {
  const grouped = new Map<string, Task[]>();
  for (const task of tasks) {
    const existing = grouped.get(task.specimen_id);
    if (existing) existing.push(task);
    else grouped.set(task.specimen_id, [task]);
  }
  return [...grouped.entries()];
}

/**
 * The stripe down the leading edge of a row, and the only thing on the screen
 * that is purely a state indicator. It reads the pending queue as well as the
 * schedule, so answering a row turns its stripe green immediately — before the
 * write has even left the phone.
 */
function RowStripe({
  specimenId,
  over,
  known,
}: {
  specimenId: string;
  over: number | null;
  known: boolean;
}) {
  const { pendingFor } = useLogger();
  const urgency = pendingFor(specimenId).length > 0
    ? "logged"
    : over !== null
      ? "overdue"
      : known
        ? "due"
        : "unknown";
  return <span className="row-stripe" data-urgency={urgency} aria-hidden="true" />;
}

export function TodayPage() {
  const { t, pick, language } = useLanguage();
  // The engine hands back codes; turning them into sentences is this side's
  // job, and the only side that knows which language is being read.
  const say = useReasonList();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [plants, setPlants] = useState<Map<string, Specimen>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const [nextPlan, specimens, allProducts] = await Promise.all([
        fetchPlan(signal),
        fetchSpecimens(signal),
        fetchProducts(signal),
      ]);
      setPlan(nextPlan);
      setProducts(allProducts);
      setPlants(new Map(specimens.map((s) => [s.id, s])));
      setError(null);
    } catch (cause) {
      if (signal?.aborted) return;
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  if (loading) return <main className="shell"><LoadingState /></main>;
  if (error || !plan) return <main className="shell"><ErrorState message={error ?? "Unknown error."} /></main>;

  const due = plan.locations.reduce((total, group) => total + group.tasks.length, 0);
  const byPlantCount = plan.locations.reduce(
    (total, group) => total + byPlant(group.tasks).length,
    0,
  );
  // Day one is when nothing has ever been logged for anything — a different
  // situation from a quiet day, and it should read as an invitation rather than
  // a backlog of nine chores.
  const dayOne =
    due > 0 &&
    plan.locations.every((group) => group.tasks.every((task) => task.days_since === null));
  const heat = heatReason(plan);

  return (
    <LoggerProvider onCommitted={() => void load()}>
    <main className="shell">
      <header className="masthead">
        <div>
          <h1>{t.today}</h1>
          {/* The date, and how much of the collection is actually asking for
              attention — so a short list reads as a short list rather than as
              the whole app. */}
          <p className="masthead-sub">
            {/* The date follows the interface language — an English date inside
                an Arabic line reads as a bug, not as bilingualism. */}
            <span>
              {new Date(plan.at).toLocaleDateString(language === "ar" ? "ar-EG" : "en-GB", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </span>
            {plants.size > 0 && <span> · {t.worthChecking(byPlantCount, plants.size)}</span>}
          </p>
        </div>
        <p className="masthead-nav">
          <Link className="nav-pill" to="/plants" aria-label={t.allPlants}>
            <MenuIcon />
          </Link>
          <LanguageToggle />
        </p>
      </header>

      {/* The one thing that moved every outdoor interval at once. */}
      {heat && (
        <p className="heat-strip">
          <SunIcon size={20} />
          <span>{say([heat])[0]}</span>
        </p>
      )}

      {due === 0 && plan.warnings.length === 0 && plan.blocked.length === 0 && (
        <div className="quiet">
          <span className="quiet-mark">
            <CheckIcon size={66} />
          </span>
          <div>
            <h2>{t.nothingToCheck}</h2>
            <p>{t.quietBody}</p>
          </div>
        </div>
      )}

      {dayOne && (
        <div className="day-one">
          <h2>{t.dayOneTitle}</h2>
          <p>{t.dayOneBody}</p>
        </div>
      )}

      {plan.locations.map((group) => (
        <section
          className="band"
          key={group.location_id}
          data-placement={group.placement}
        >
          <div className="band-head">
            {group.placement === "outdoor" ? <SunIcon /> : <RoomIcon />}
            <div className="band-names">
              <h2 lang="ar">{group.name_ar}</h2>
              <p className="band-meta">
                {group.name_en} · {group.placement === "outdoor" ? t.outdoor : t.indoor}
              </p>
            </div>
            <span className="band-count">
              {byPlant(group.tasks).length} {t.toTend}
            </span>
          </div>

          <ul className="plant-rows">
            {/* One card per plant, not per task: two things to do to the same
                plant is one trip, not two. */}
            {byPlant(group.tasks).map(([specimenId, tasks]) => {
              const plant = plants.get(specimenId);
              const check = tasks.find((task) => task.kind === "check");
              // Every feeding, not the first one. Both products can fall due on
              // the same day — the seven-day separation only starts once one of
              // them actually goes on — and showing one of two silently drops a
              // real ask.
              const feeds = tasks.filter((task) => task.kind === "feed");
              const over = check ? daysOver(check.due_at, plan.at) : null;
              const known = tasks.some((task) => task.days_since !== null);
              const photo = photoFor(plant?.species.scientific_name);
              const flags = flagsFor(plant, t);
              // The ask and the engine's reasoning are one sentence, not two
              // stacked lines: "why · last checked 3 days ago — compacted soil
              // holds water longer".
              const reasons = say(worthShowing(check?.reasons ?? feeds[0]?.reasons));
              return (
                <li key={specimenId} className="plant-row">
                  <RowStripe specimenId={specimenId} over={over} known={known} />

                  <div className="row-main">
                    {/* The photograph is the plant's name. On a phone it is an
                        identifier; on a desk it becomes evidence. */}
                    {photo ? (
                      <img className="row-photo" src={photo} alt="" loading="lazy" />
                    ) : (
                      <span className="row-photo placeholder" aria-hidden="true" />
                    )}

                    <div className="row-detail">
                      <div className="row-head">
                        <div className="row-name">
                          {/* Arabic leads in both language modes: it is what he
                              calls the plant. Then the name the world uses, then
                              the one the books file it under. */}
                          <Link to={`/plants/${specimenId}`} lang="ar" className="task-name">
                            {plant?.species.name.ar || "—"}
                          </Link>
                          {/* A Latin run inside an RTL page needs isolating, or
                              trailing punctuation jumps to the front: "Thymus
                              spp." renders as ".Thymus spp". */}
                          <span className="task-common" dir="ltr">
                            {plant?.species.name.en}
                          </span>
                          <span className="task-en" dir="ltr">
                            {plant?.species.scientific_name}
                          </span>
                        </div>

                        {/* The only terracotta on a page where nothing is late. */}
                        {over !== null ? (
                          <span className="overdue" aria-label={`${over} ${t.daysOver}`}>
                            {/* Isolated: inside an RTL page the bidi algorithm
                                otherwise moves the sign, rendering "+25" as
                                "25+". */}
                            <strong dir="ltr">+{over}</strong>
                            <span>{t.daysOver}</span>
                          </span>
                        ) : check ? (
                          <span className="due-today">{t.dueToday}</span>
                        ) : (
                          feeds.length > 0 && (
                            <span className="due-today feed">{t.feedDue}</span>
                          )
                        )}
                      </div>

                      <div className="row-body">
                        {flags.length > 0 && (
                          <ul className="flags">
                            {flags.map((flag) => (
                              <li key={flag.label} className="flag" data-tone={flag.tone}>
                                {flag.label}
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Every recommendation says why it is being made. */}
                        {check && (
                          <p className="task-why">
                            <span className="why-label">{t.why} · </span>
                            {since(check.days_since, t)}
                            {reasons.length > 0 && (
                              <span className="why-reason"> — {reasons.join(", ")}</span>
                            )}
                          </p>
                        )}

                        {/* The dose is read off the bottle, so the bottle is here. */}
                        {feeds.map((feed) => (
                          <div className="feed-ask" key={feed.product_id ?? feed.product}>
                            {productPhotoFor(feed.product) && (
                              <img src={productPhotoFor(feed.product) ?? ""} alt="" loading="lazy" />
                            )}
                            <div>
                              {/* Only the product and its dose are a Latin run.
                                  Isolating the whole line would move the Arabic
                                  verb to the wrong end of it. */}
                              <div className="feed-what">
                                {t.feedWith}{" "}
                                <span dir="ltr">
                                  {feed.product} — {feed.dose}
                                </span>
                              </div>
                              {say(worthShowing(feed.reasons)).length > 0 && (
                                <div className="feed-rule">
                                  {say(worthShowing(feed.reasons)).join(", ")}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}

                        {!check && feeds.length === 0 && (
                          <p className="task-ask">{since(null, t)}</p>
                        )}
                      </div>
                    </div>

                    <QuickLog
                      specimenId={specimenId}
                      plantName={pick(plant?.species.name) || ""}
                      products={products}
                      /* Every product that is due, with its pot dose — so the
                         picker marks both when both fall due on the same day. */
                      dueDoses={Object.fromEntries(
                        feeds
                          .filter((feed) => feed.product_id)
                          .map((feed) => [feed.product_id as string, feed.dose ?? ""]),
                      )}
                      /* Only one control on a row is filled, and it is the one
                         the app is actually asking for. Once the check is
                         satisfied and only a feeding is left, Watered is no
                         longer the thing to reach for. */
                      leadWith={!check && feeds.length > 0 ? "feed" : "water"}
                      /* The bar is an ask. A row with no Check due is not
                         asking, so it folds down to the reason for the
                         silence. */
                      checkDue={Boolean(check)}
                      lastSoil={(check ?? feeds[0])?.last_soil}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      <NotAsking blocked={plan.blocked} plants={plants} />

      {/* A plant somewhere its Species has no interval for. It produces no task
          at all, so this card is the only thing that knows it exists. */}
      <WrongPlace
        warnings={plan.warnings.filter((warning) => !warning.accepted)}
        plants={plants}
      />
      <UndoBar />
    </main>
    </LoggerProvider>
  );
}
